import React from 'react';
import { motion } from 'framer-motion';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const TrustBadges = () => {
  const qualityCommitments = [
    {
      id: 1,
      name: "Quality-First Approach",
      description: "Our commitment to premium quality in all agricultural products",
      icon: "Award",
      category: "Quality",
      status: "Active"
    },
    {
      id: 2,
      name: "Sustainable Practices",
      description: "Environmental responsibility in our sourcing and processing",
      icon: "Leaf",
      category: "Environment",
      status: "Active"
    },
    {
      id: 3,
      name: "Safe Processing",
      description: "Food safety standards in all our operations",
      icon: "Shield",
      category: "Safety",
      status: "Active"
    },
    {
      id: 4,
      name: "Ethical Sourcing",
      description: "Fair practices with Nigerian agricultural communities",
      icon: "Heart",
      category: "Ethics",
      status: "Active"
    },
    {
      id: 5,
      name: "Product Testing",
      description: "Rigorous quality testing before export",
      icon: "CheckCircle",
      category: "Testing",
      status: "Active"
    },
    {
      id: 6,
      name: "Transparency",
      description: "Open communication throughout our export process",
      icon: "Eye",
      category: "Trust",
      status: "Active"
    }
  ];

  const serviceProviders = [
    {
      id: 1,
      name: "Nigerian Logistics",
      category: "Local Transport",
      description: "Professional logistics for product collection and transport within Nigeria"
    },
    {
      id: 2,
      name: "Export Documentation",
      category: "Documentation Services",
      description: "Professional export documentation and customs clearance services"
    },
    {
      id: 3,
      name: "Quality Testing Labs",
      category: "Testing Services",
      description: "Certified laboratories for product quality verification"
    },
    {
      id: 4,
      name: "Agricultural Cooperatives",
      category: "Source Partners",
      description: "Direct partnerships with Nigerian agricultural cooperatives"
    },
    {
      id: 5,
      name: "Processing Facilities",
      category: "Processing Partners",
      description: "Certified facilities for value-added product processing"
    },
    {
      id: 6,
      name: "Freight Forwarders",
      category: "Shipping Services",
      description: "International freight and shipping service providers"
    }
  ];

  const preparationAreas = [
    {
      id: 1,
      name: "Export License",
      description: "Full export licenses for Nigerian agricultural products",
      status: "Active",
      icon: "FileText"
    },
    {
      id: 2,
      name: "Quality Standards",
      description: "Established quality control processes and testing procedures",
      status: "Active",
      icon: "CheckSquare"
    },
    {
      id: 3,
      name: "Supplier Network",
      description: "Established relationships with reliable Nigerian agricultural suppliers",
      status: "Active",
      icon: "Users"
    },
    {
      id: 4,
      name: "Compliance Framework",
      description: "Complete procedures for international trade compliance",
      status: "Active",
      icon: "Shield"
    }
  ];

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-primary mb-6">
            Our Commitment to Excellence
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            We've built our agricultural export business on strong foundations of quality, transparency, and ethical practices in Nigerian agriculture.
          </p>
        </motion.div>

        {/* Quality Commitments Grid */}
        <div className="mb-20">
          <motion.h3
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-2xl font-bold text-primary text-center mb-12"
          >
            Our Quality Commitments
          </motion.h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {qualityCommitments.map((commitment, index) => (
              <motion.div
                key={commitment.id}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1, ease: "easeOut" }}
                className="glass-card p-6 hover:shadow-elevation-md transition-all duration-300 group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center">
                    <Icon name={commitment.icon} size={24} color="white" />
                  </div>
                  <span className="px-2 py-1 bg-primary-100 text-primary text-xs font-medium rounded-full">
                    {commitment.category}
                  </span>
                </div>
                
                <h4 className="text-lg font-bold text-primary mb-2">{commitment.name}</h4>
                <p className="text-text-secondary mb-4 text-sm">{commitment.description}</p>
                
                <div className="flex items-center text-sm">
                  <span className="px-2 py-1 bg-accent-100 text-accent text-xs font-medium rounded-full">
                    {commitment.status}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Service Providers */}
        <div className="mb-20">
          <motion.h3
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-2xl font-bold text-primary text-center mb-12"
          >
            Our Service Network
          </motion.h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {serviceProviders.map((provider, index) => (
              <motion.div
                key={provider.id}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1, ease: "easeOut" }}
                className="glass-card p-6 text-center hover:shadow-elevation-md transition-all duration-300 group"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-accent to-primary rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Icon name="Building" size={28} color="white" />
                </div>
                <h4 className="font-semibold text-primary text-lg mb-2">{provider.name}</h4>
                <p className="text-sm text-accent font-medium mb-3">{provider.category}</p>
                <p className="text-xs text-text-secondary">{provider.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Preparation Areas */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="glass-card p-8 mb-20"
        >
          <h3 className="text-2xl font-bold text-primary text-center mb-8">
            Our Export Foundation
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {preparationAreas.map((prep, index) => (
              <motion.div
                key={prep.id}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1, ease: "easeOut" }}
                className="flex items-start space-x-4 p-4 bg-surface-100 rounded-lg"
              >
                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name={prep.icon} size={20} color="white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-primary">{prep.name}</h4>
                    <span className="px-2 py-1 bg-accent-100 text-accent text-xs font-medium rounded-full">
                      {prep.status}
                    </span>
                  </div>
                  <p className="text-text-secondary text-sm">{prep.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
          
          <div className="mt-8 pt-6 border-t border-surface-200 text-center">
            <p className="text-text-secondary mb-4">
              We're committed to maintaining the highest standards in our export operations and building lasting partnerships.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
              <a 
                href="https://wa.me/2347061704302"
                target="_blank" 
                rel="noopener noreferrer"
                className="px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors duration-250 flex items-center space-x-2"
              >
                <Icon name="MessageCircle" size={18} />
                <span>Chat on WhatsApp</span>
              </a>
            </div>
          </div>
        </motion.div>

        {/* Trust Summary */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
          className="text-center"
        >
          <h3 className="text-2xl font-bold text-primary mb-4">
            Building Trust Through Transparency
          </h3>
          <p className="text-text-secondary mb-8 max-w-3xl mx-auto">
            We believe in honest business practices. As an agricultural export company, we're committed to earning your trust through quality products, transparent processes, and reliable service.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            <div className="flex items-center space-x-2 text-primary">
              <Icon name="Heart" size={20} />
              <span className="font-medium">Established & Honest</span>
            </div>
            <div className="flex items-center space-x-2 text-accent">
              <Icon name="Leaf" size={20} />
              <span className="font-medium">Agricultural Focus</span>
            </div>
            <div className="flex items-center space-x-2 text-success">
              <Icon name="Users" size={20} />
              <span className="font-medium">Nigerian Sourced</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TrustBadges;